/**
 * ExamSettingsModal.js
 * Org-only modal to configure:
 *  - Proctor permissions (what proctors can do)
 *  - Grading logic (custom grade bands)
 *  - Student result download permission
 *  - Q&A visibility
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const DEFAULT_GRADING = [
  { min:90, max:100, grade:'A+' },
  { min:80, max:89,  grade:'A'  },
  { min:70, max:79,  grade:'B'  },
  { min:60, max:69,  grade:'C'  },
  { min:50, max:59,  grade:'D'  },
  { min:0,  max:49,  grade:'F'  },
];

function Toggle({ label, description, checked, onChange }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
      <div>
        <div style={{ fontSize:14, fontWeight:600 }}>{label}</div>
        {description && <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:2 }}>{description}</div>}
      </div>
      <button onClick={() => onChange(!checked)} style={{
        width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
        background: checked ? 'var(--accent-primary)' : 'var(--bg-elevated)',
        boxShadow: checked ? '0 0 0 2px rgba(79,142,247,0.3)' : '0 0 0 1px var(--border)',
        position:'relative', transition:'all 0.2s', flexShrink:0,
      }}>
        <span style={{
          position:'absolute', top:3, left: checked ? 23 : 3,
          width:18, height:18, borderRadius:9,
          background: checked ? '#fff' : 'var(--text-muted)',
          transition:'left 0.2s',
        }} />
      </button>
    </div>
  );
}

export default function ExamSettingsModal({ exam, onClose, onSaved }) {
  const [settings, setSettings] = useState({
    proctor_can_download_report:  exam.proctor_can_download_report !== false,
    proctor_can_see_results:      exam.proctor_can_see_results !== false,
    proctor_can_download_qa:      exam.proctor_can_download_qa === true,
    student_can_download_result:  exam.student_can_download_result !== false,
    questions_hidden:             exam.questions_hidden === true,
    show_student_photos:          exam.show_student_photos !== false,
    allow_floating_settings:      exam.allow_floating_settings !== false,
  });

  // Parse grading logic
  const parseGrading = () => {
    try {
      if (typeof exam.grading_logic === 'string') return JSON.parse(exam.grading_logic);
      if (Array.isArray(exam.grading_logic)) return exam.grading_logic;
    } catch {}
    return DEFAULT_GRADING;
  };

  const [grading, setGrading] = useState(parseGrading());
  const [saving,  setSaving]  = useState(false);
  const [tab,     setTab]     = useState('permissions'); // permissions | grading

  const set = (key) => (val) => setSettings(p => ({ ...p, [key]: val }));

  const updateGrade = (i, field, value) => {
    setGrading(g => g.map((row, idx) => idx === i ? { ...row, [field]: field === 'grade' ? value : parseInt(value) || 0 } : row));
  };

  const addBand = () => setGrading(g => [...g, { min:0, max:49, grade:'F' }]);
  const removeBand = (i) => setGrading(g => g.filter((_,idx) => idx !== i));

  const save = async () => {
    // Validate grading — no overlapping ranges
    setSaving(true);
    try {
      await axios.patch(`/api/exams/${exam.id}/settings`, {
        ...settings,
        show_student_photos: settings.show_student_photos,
        allow_floating_settings: settings.allow_floating_settings,
        grading_logic: grading,
      });
      toast.success('Exam settings saved');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:560, maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h3 className="modal-title" style={{ margin:0 }}>⚙️ Exam Settings</h3>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:3 }}>{exam.title}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          {[['permissions','🔐 Permissions'],['grading','🎓 Grading']].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} className="btn btn-secondary btn-sm"
              style={tab===k ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {l}
            </button>
          ))}
        </div>

        <div style={{ overflowY:'auto', flex:1 }}>

          {/* Permissions tab */}
          {tab === 'permissions' && (
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:12 }}>
                Proctor Permissions
              </div>
              <Toggle
                label="Can Download Attendance Report"
                description="Proctors can export the full attendance report as PDF/Excel"
                checked={settings.proctor_can_download_report}
                onChange={set('proctor_can_download_report')}
              />
              <Toggle
                label="Can View Student Results"
                description="Proctors can see individual student result details and scores"
                checked={settings.proctor_can_see_results}
                onChange={set('proctor_can_see_results')}
              />
              <Toggle
                label="Can Download Questions & Answers"
                description="Proctors can download the full Q&A paper with correct answers"
                checked={settings.proctor_can_download_qa}
                onChange={set('proctor_can_download_qa')}
              />
              <Toggle
                label="Q&A Hidden from Proctor"
                description="Hide questions and correct answers from proctor's exam view"
                checked={settings.questions_hidden}
                onChange={set('questions_hidden')}
              />

              <div style={{ fontSize:12, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:12, marginTop:20 }}>
                Student Permissions
              </div>
              <Toggle
                label="Student Can Download Result PDF"
                description="After exam submission, student can download their result as PDF"
                checked={settings.student_can_download_result}
                onChange={set('student_can_download_result')}
              />
              <Toggle
                label="Show Student Photos in Exam"
                description="Display student profile photo in the exam interface (if uploaded)"
                checked={settings.show_student_photos}
                onChange={set('show_student_photos')}
              />
              <Toggle
                label="Allow Floating Settings Panel"
                description="Students can change font size and dark/light theme during the exam"
                checked={settings.allow_floating_settings}
                onChange={set('allow_floating_settings')}
              />
            </div>
          )}

          {/* Grading tab */}
          {tab === 'grading' && (
            <div>
              <div className="alert alert-info" style={{ marginBottom:16, fontSize:12 }}>
                Define grade bands based on percentage score. Lower bands should have lower min values.
                Gaps or overlaps are allowed — the first matching band is used.
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:8, marginBottom:8 }}>
                <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600 }}>Min %</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600 }}>Max %</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600 }}>Grade</div>
                <div />
              </div>
              {grading.map((band, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:8, marginBottom:8, alignItems:'center' }}>
                  <input className="form-input" type="number" min="0" max="100" value={band.min}
                    onChange={e => updateGrade(i,'min',e.target.value)}
                    style={{ padding:'7px 10px', fontSize:13 }} />
                  <input className="form-input" type="number" min="0" max="100" value={band.max}
                    onChange={e => updateGrade(i,'max',e.target.value)}
                    style={{ padding:'7px 10px', fontSize:13 }} />
                  <input className="form-input" type="text" value={band.grade}
                    onChange={e => updateGrade(i,'grade',e.target.value)}
                    style={{ width:60, padding:'7px 10px', fontSize:13, fontWeight:700, textAlign:'center' }} />
                  <button className="btn btn-danger btn-sm" style={{ fontSize:12 }} onClick={() => removeBand(i)}>✕</button>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={addBand} style={{ marginTop:8 }}>+ Add Band</button>

              {/* Preview */}
              <div style={{ marginTop:16, padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                <div style={{ fontSize:12, fontWeight:600, marginBottom:8 }}>Preview</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {grading.map((band, i) => (
                    <span key={i} style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700, background:'rgba(79,142,247,0.1)', color:'var(--accent-primary)', border:'1px solid rgba(79,142,247,0.2)' }}>
                      {band.min}–{band.max}% → {band.grade}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20, paddingTop:16, borderTop:'1px solid var(--border)' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>
            {saving ? 'Saving...' : '✓ Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
